import { useRouter } from "@core/Api/useRouter";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import { refreshPage } from "@core-ui/utils/initGlobalFuncs";
import { toast } from "@ui-kit/Toast";
import { useMemo, useRef } from "react";
import type { ReloadResult, ToolsLinks, ToolsType, WorkspaceToolsState } from "../../model/types";
import { type ApiResult, useWorkspaceToolsApi } from "../api/workspaceToolsApi";
import { tui } from "../locale";
import { showWaitingTools } from "../root/showLandedTools";
import { isUpdateFailure } from "../root/useUnseenError";
import { type LoadingKind, useWorkspaceToolsStore } from "./useWorkspaceToolsStore";
import { whenSaved } from "./whenSaved";

/** An unconfirmed reload counts first, and may only ask to confirm: a quick question shows no toast. */
const COUNTING_MS = 400;

const startLoadingAfter = (delayMs: number, kind: LoadingKind) => {
	let done: (() => void) | null = null;
	const timer = setTimeout(() => {
		done = useWorkspaceToolsStore.getState().startLoading(kind);
	}, delayMs);
	return () => {
		clearTimeout(timer);
		done?.();
	};
};

export const useWorkspaceToolsActions = () => {
	const api = useWorkspaceToolsApi();
	const router = useRouter();
	const routerRef = useRef(router);
	routerRef.current = router;
	const apiUrlCreator = ApiUrlCreatorService.value;
	const apiUrlCreatorRef = useRef(apiUrlCreator);
	apiUrlCreatorRef.current = apiUrlCreator;

	return useMemo(() => {
		const store = () => useWorkspaceToolsStore.getState();

		const forCurrent = async <T>(run: () => Promise<ApiResult<T>>): Promise<T | null> => {
			const { workspace } = store();
			const result = await run();
			return result.ok && store().workspace === workspace ? result.value : null;
		};

		const applied = async (run: () => Promise<ApiResult<WorkspaceToolsState>>) => {
			const { workspace } = store();
			const state = await forCurrent(run);
			if (state) store().setState(workspace, state);
			return state;
		};

		const refresh = () => applied(api.getState);

		const withLoading = async (run: () => Promise<ApiResult<WorkspaceToolsState>>) => {
			const done = store().startLoading();
			try {
				return await applied(run);
			} finally {
				done();
			}
		};

		/** `silent` for a signed-out user: the server clones nothing, it only learns there is no source. */
		const ensure = (quiet = false, silent = false) => (silent ? applied : withLoading)(() => api.ensure(quiet));

		/** The server decides whether the loss of unpublished changes needs a confirmation; the client only relays it. */
		const reload = async (confirmed?: string) => {
			store().setReloadConfirm(null);
			await whenSaved();
			const { workspace } = store();
			const done = confirmed ? store().startLoading("reload") : startLoadingAfter(COUNTING_MS, "reload");
			let result: ReloadResult | null;
			try {
				result = await forCurrent(() => api.reload(confirmed));
			} finally {
				done();
			}
			if (!result) return;
			if (result.kind === "done" && !result.refusal) {
				store().setState(workspace, result.state);
				return refreshPage();
			}
			if (result.kind === "confirm") return store().setReloadConfirm({ unpublished: result.unpublished });
			void showWaitingTools(apiUrlCreatorRef.current);
			store().setState(workspace, result.state);
			store().showRefusal(result.refusal);
		};

		const explainMissingLink = (state: WorkspaceToolsState) => {
			if (!state.enabled) return;
			if (state.error && !isUpdateFailure(state.error)) store().showErrorAgain();
			else if (state.error || !state.onDisk) toast(tui("open.not-loaded"), { status: "info" });
		};

		const openLink = async (key: keyof ToolsLinks) => {
			const current = store().state;
			const state = current.enabled && !current.links[key] ? await ensure() : current;
			if (!state?.enabled) return;
			const link = state.links[key];
			if (link) await routerRef.current.pushPath(link);
			else explainMissingLink(state);
		};

		const findToolLink = async (type: ToolsType, id: string) => {
			const link = await forCurrent(() => api.getLink(type, id));
			if (link) return link;
			if (!(await ensure())) return null;
			return forCurrent(() => api.getLink(type, id));
		};

		const openTool = async (type: ToolsType, id: string) => {
			const link = await findToolLink(type, id);
			if (link) await routerRef.current.pushPath(link.pathname);
			else explainMissingLink(store().state);
		};

		return { refresh, ensure, reload, openLink, openTool };
	}, [api]);
};
